import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

/**
 * TanStack Query setup.
 *
 * Defaults follow the Dozka-Dashboard convention :
 *   - 30s staleTime (server state is "fresh" for 30s, no background refetch)
 *   - 5min gcTime (cache eviction after 5min idle)
 *   - retry once, but never on 4xx (client errors won't fix themselves)
 *   - no refetch on window focus / mount  → predictable, panel-local invalidation
 */
export function QueryProvider({ children }: { children: ReactNode }) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            gcTime: 5 * 60_000,
            retry: (failureCount, error) => {
              const status = (error as { status?: number })?.status;
              if (status && status >= 400 && status < 500) return false;
              return failureCount < 1;
            },
            refetchOnWindowFocus: false,
            refetchOnMount: false,
          },
        },
      })
  );
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
